import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchOrders, fetchOrderById, setSelectedOrder } from '../../store/slices/ordersSlice';
import { fetchUsers } from '../../store/slices/usersSlice';
import apiService from '../../services/api';
import {
  subscribeToDeliveryPersonsUpdates,
  subscribeToOrdersUpdates,
} from '../../services/socket';
import { Eye, Download, X, Copy, Package, Printer, Loader2 } from 'lucide-react';
import { exportOrdersToExcel } from '../../utils/excelExport';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../contexts/ToastContext';
import { formatDateTime, formatCurrency } from '../../utils/format';
import { getTokens } from '../../utils/tokens';
import {
  PageHeader,
  StatStrip,
  GhostBtn,
  PrimaryBtn,
  Toolbar,
  FilterChip,
  StatusPill,
  DataTable,
  Pagination,
  MonoMoney,
  AvatarInitials,
  MethodPill,
} from '../ui/AdminPagePrimitives';
import { StatStripSkeleton, TableSkeleton } from '../ui/Skeleton';
import { OrderItemComponentsList } from '../ui/OrderItemComponentsList';
import { printReceipt } from '../pos/Receipt';

function storeInfoForPrintFromGetStore(data) {
  const d = data || {};
  let websiteObj = d.website;
  if (typeof websiteObj === 'string') {
    try {
      const parsed = JSON.parse(websiteObj);
      websiteObj = parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      websiteObj = { url: websiteObj, include_in_receipt: 'none' };
    }
  }
  let phoneNumbers = [];
  if (d.phone_numbers && typeof d.phone_numbers === 'object') {
    phoneNumbers = Object.entries(d.phone_numbers)
      .map(([key, val]) => {
        if (typeof val === 'boolean') return val ? key : null;
        if (typeof val === 'object' && val !== null && val.include_in_receipt !== false) return val.number || key;
        return null;
      })
      .filter(Boolean);
  }
  let socialMediaUrls = [];
  if (d.social_media_urls && typeof d.social_media_urls === 'object') {
    socialMediaUrls = Object.entries(d.social_media_urls)
      .filter(([, val]) => {
        if (typeof val === 'object' && val !== null) return val.include_in_receipt !== false;
        return true;
      })
      .map(([platform, val]) => ({
        platform,
        url: typeof val === 'object' && val !== null ? val.url || val : val,
      }))
      .filter((s) => s.url);
  }
  const ls = parseInt(d.logo_size, 10);
  return {
    storeName: d.name || '',
    receiptFooter: d.receipt_footer || '',
    website: websiteObj && typeof websiteObj === 'object' ? websiteObj : null,
    phoneNumbers,
    socialMediaUrls,
    logoPath: d.logo_path || '',
    logoSize: Number.isFinite(ls) && ls >= 40 && ls <= 400 ? ls : 100,
    includeDeliveryFee: d.include_delivery_fee === true,
  };
}

// Convert Arabic-Indic (٠-٩) and Persian (۰-۹) numerals to ASCII 0-9.
const normalizeDigits = (s) =>
  s
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0));

// Auto-detect each space-separated token; tokens combine with AND.
//   contains a non-digit (e.g. "R-…")   -> receipt
//   all digits starting with 0 ("01…")  -> phone (any length)
//   all digits otherwise                -> session number (any length)
const parseOrderSearch = (text) => {
  const out = { customer_phone: '', session_number: '', receipt_num: '' };
  for (const raw of text.trim().split(/\s+/).filter(Boolean)) {
    const tok = normalizeDigits(raw);
    if (!/^\d+$/.test(tok)) {
      out.receipt_num = tok;
    } else if (tok.startsWith('0')) {
      out.customer_phone = tok;
    } else {
      out.session_number = tok;
    }
  }
  return out;
};

const Reports = () => {
  const dispatch = useDispatch();
  const { list: transactions, loading, error, selectedOrder } = useSelector((state) => state.orders);
  const { list: users } = useSelector((state) => state.users);
  const { mode } = useSelector((state) => state.theme);
  const { t, isRTL, language } = useLanguage();
  const showToast = useToast();

  const tk = getTokens(mode);

  const [filters, setFilters] = useState({
    cashier_id: 'all',
    payment_method: 'all',
    order_type: 'all',
    session_number: '',
    status: 'all',
    customer_phone: '',
    receipt_num: '',
    has_discount: 'all',
    delivery_person_id: 'all',
    start_date: '',
    end_date: '',
  });

  const [searchText, setSearchText] = useState('');

  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [receiptCopied, setReceiptCopied] = useState(false);
  const [deliveryPersons, setDeliveryPersons] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [orderPrintLoading, setOrderPrintLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    setReceiptCopied(false);
  }, [selectedTransaction]);

  useEffect(() => {
    dispatch(fetchUsers(true));
  }, [dispatch]);

  const loadDeliveryPersons = useCallback(async () => {
    try {
      const res = await apiService.getDeliveryPersons();
      setDeliveryPersons(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      console.error('Failed to load delivery persons', e);
    }
  }, []);

  const refetchOrders = useCallback(() => {
    dispatch(fetchOrders({ limit: 1000, ...filters }));
  }, [dispatch, filters]);

  useEffect(() => { refetchOrders(); }, [refetchOrders]);

  useEffect(() => {
    const unsubscribe = subscribeToOrdersUpdates(refetchOrders);
    return unsubscribe;
  }, [refetchOrders]);

  useEffect(() => {
    if (selectedOrder) setSelectedTransaction(selectedOrder);
  }, [selectedOrder]);

  useEffect(() => {
    return () => {
      setSelectedTransaction(null);
      dispatch(setSelectedOrder(null));
    };
  }, [dispatch]);

  // ── Derived stats ──────────────────────────────────────────────────────────
  const allTx = transactions || [];

  const today = new Date().toISOString().split('T')[0];

  const todayCount = allTx.filter(
    (tx) => (tx.created_at || '').split('T')[0] === today
  ).length;

  const totalRevenue = allTx.reduce(
    (sum, tx) => sum + parseFloat(tx.total_amount || 0), 0
  );

  const avgBasket = allTx.length > 0 ? totalRevenue / allTx.length : 0;

  const refundedCount = allTx.filter(
    (tx) => tx.status === 'refunded' || tx.status === 'partially_refunded'
  ).length;

  // ── Pagination ─────────────────────────────────────────────────────────────
  const filteredTx = allTx;

  const totalPages = Math.ceil(filteredTx.length / itemsPerPage) || 1;

  const currentTransactions = React.useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredTx.slice(start, start + itemsPerPage);
  }, [filteredTx, currentPage, itemsPerPage]);

  useEffect(() => { setCurrentPage(1); }, [filters]);

  const handleViewOrder = (order) => {
    dispatch(fetchOrderById(order.id));
  };

  const handlePrintOrderDetail = async () => {
    if (!selectedTransaction) return;
    setOrderPrintLoading(true);
    try {
      const storeRes = await apiService.getStore();
      const storeData = storeRes?.data || storeRes || {};
      const storeInfo = storeInfoForPrintFromGetStore(storeData);
      await printReceipt(selectedTransaction, storeInfo);
    } catch (err) {
      console.error('Print order from reports:', err);
      showToast(typeof err === 'string' ? err : err?.message || t('alerts.failedToPrintReceipt'), "error");
    } finally {
      setOrderPrintLoading(false);
    }
  };

  const cashiers = (users || []).filter((u) => u.role === 'cashier');

  const clearFilters = () => {
    setFilters({
      cashier_id: 'all',
      payment_method: 'all',
      order_type: 'all',
      session_number: '',
      status: 'all',
      customer_phone: '',
      receipt_num: '',
      has_discount: 'all',
      delivery_person_id: 'all',
      start_date: '',
      end_date: '',
    });
    setSearchText('');
  };

  const hasActiveFilters =
    filters.cashier_id !== 'all' ||
    filters.payment_method !== 'all' ||
    filters.order_type !== 'all' ||
    filters.session_number.trim() !== '' ||
    filters.status !== 'all' ||
    (filters.customer_phone || '').trim() !== '' ||
    (filters.receipt_num || '').trim() !== '' ||
    filters.has_discount !== 'all' ||
    filters.delivery_person_id !== 'all' ||
    !!filters.start_date ||
    !!filters.end_date;

  // ── Status translation helpers ─────────────────────────────────────────────
  const getStatusKind = (status) => {
    if (status === 'completed') return 'paid';
    if (status === 'refunded' || status === 'partially_refunded') return 'refunded';
    return 'pending';
  };

  const getStatusLabel = (status) => {
    if (status === 'completed') return t('reports.completed');
    if (status === 'refunded') return t('reports.refunded');
    if (status === 'partially_refunded') return t('reports.partiallyRefunded');
    return status || '—';
  };

  const handleExport = async () => {
    if (!filteredTx.length) return;
    setExporting(true);
    try {
      const labels = t('reports.excelExport');
      const L = typeof labels === 'object' && labels !== null ? labels : {};
      const today = new Date().toISOString().split('T')[0];
      const paymentLabels = {
        cash: t('pos.cash'),
        card: t('pos.card'),
        instapay: t('pos.instapay'),
        wallet: t('pos.wallet'),
        mixed: t('pos.mixed'),
      };
      await exportOrdersToExcel(filteredTx, `orders-${today}.xlsx`, {
        ...L,
        formatPaymentMethod: (method) => {
          const key = String(method || '').toLowerCase();
          return paymentLabels[key] || (method == null ? '' : String(method));
        },
        formatOrderStatus: getStatusLabel,
      });
    } catch (err) {
      console.error('Export failed', err);
    } finally {
      setExporting(false);
    }
  };

  // ── Filter select options ──────────────────────────────────────────────────
  const paymentMethodOptions = [
    { value: 'all', label: t('reports.allPaymentMethods') },
    { value: 'cash', label: t('pos.cash') },
    { value: 'card', label: t('pos.card') },
    { value: 'instapay', label: t('pos.instapay') },
    { value: 'wallet', label: t('pos.wallet') },
    { value: 'mixed', label: t('pos.mixed') },
  ];

  const statusOptions = [
    { value: 'all', label: t('reports.allStatuses') },
    { value: 'completed', label: t('reports.completed') },
    { value: 'refunded', label: t('reports.refunded') },
    { value: 'partially_refunded', label: t('reports.partiallyRefunded') },
  ];

  const orderTypeOptions = [
    { value: 'all', label: t('common.all') },
    { value: 'delivery', label: t('pos.delivery') },
    { value: 'in_store', label: t('pos.inStore') },
  ];

  const discountOptions = [
    { value: 'all', label: t('common.all') },
    { value: 'yes', label: t('reports.withDiscount') },
    { value: 'no', label: t('reports.withoutDiscount') },
  ];

  const cashierOptions = [
    { value: 'all', label: t('sessions.allCashiers') },
    ...cashiers.map((c) => ({ value: String(c.id), label: c.full_name })),
  ];

  const deliveryOptions = [
    { value: 'all', label: t('common.all') },
    ...deliveryPersons.map((dp) => ({ value: String(dp.id), label: dp.name })),
  ];

  const ordersFilterLabelStyle = {
    fontSize: 10.5,
    color: tk.textMuted,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  };

  // ── Table columns ──────────────────────────────────────────────────────────
  const cols = [
    { key: 'receipt', label: t('reports.orderNo') },
    { key: 'datetime', label: `${t('common.date')} / ${t('common.time')}` },
    {
      key: 'cashier',
      label: language === 'ar'
        ? <span style={{ display: 'inline-block', paddingRight: 15 }}>الكاشير</span>
        : t('sessions.cashier'),
    },
    { key: 'items', label: t('common.items') },
    { key: 'method', label: t('reports.paymentMethod') },
    { key: 'total', label: t('common.total') },
    { key: 'status', label: t('common.status') },
    { key: 'actions', label: '' },
  ];

  // ── Style helpers ──────────────────────────────────────────────────────────
  const fontFamily = language === 'ar' ? '"Cairo", sans-serif' : '"DM Sans", sans-serif';

  const pageStyle = {
    background: tk.bg,
    color: tk.text,
    padding: 20,
    minHeight: '100%',
    fontFamily,
    overflowY: 'auto',
  };

  const actionIconBtnStyle = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 30, height: 30, borderRadius: 6,
    background: tk.sunken, border: `1px solid ${tk.border}`,
    color: tk.textSecondary, cursor: 'pointer',
  };

  const overlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50,
  };

  const modalBoxStyle = {
    background: tk.surface, border: `1px solid ${tk.border}`,
    borderRadius: 14, maxWidth: 680, width: '100%', margin: '0 16px',
    maxHeight: '90vh', display: 'flex', flexDirection: 'column',
    boxShadow: tk.shadowLg,
  };

  const cancelBtnStyle = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '0 16px', height: 36,
    background: 'transparent', border: `1px solid ${tk.border}`,
    color: tk.textSecondary, borderRadius: 8, cursor: 'pointer',
    fontFamily: 'inherit', fontSize: 13, fontWeight: 500,
  };

  const renderCell = (row, key) => {
    switch (key) {
      case 'receipt':
        return (
          <span style={{
            fontFamily: '"JetBrains Mono", monospace', fontWeight: 700,
            fontSize: 12, color: tk.accentText,
          }}>
            {row.receipt_num || `#${row.id}`}
          </span>
        );
      case 'datetime':
        return (
          <span style={{ color: tk.textSecondary, fontSize: 12 }}>
            {formatDateTime(row.created_at)}
          </span>
        );
      case 'cashier':
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <AvatarInitials name={row.cashier_name || '?'} tk={tk} size={24} />
            <span style={{ color: tk.text, fontSize: 12.5 }}>
              {row.cashier_name || '—'}
            </span>
          </div>
        );
      case 'items':
        return (
          <span style={{
            fontFamily: '"JetBrains Mono", monospace', fontSize: 12.5,
            color: tk.text, fontWeight: 600,
          }}>
            {(row.items || []).length}
          </span>
        );
      case 'method':
        return <MethodPill tk={tk} method={row.payment_method || 'cash'} t={t} />;
      case 'total':
        return <MonoMoney amount={row.total_amount} tk={tk} size={12.5} weight={600} />;
      case 'status':
        return (
          <StatusPill tk={tk} kind={getStatusKind(row.status)}>
            {getStatusLabel(row.status)}
          </StatusPill>
        );
      case 'actions':
        return (
          <button
            style={actionIconBtnStyle}
            onClick={() => handleViewOrder(row)}
            title={t('common.view')}
          >
            <Eye size={14} />
          </button>
        );
      default:
        return null;
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={pageStyle} dir={isRTL ? 'rtl' : 'ltr'} className={`theme-${mode}`}>

      <PageHeader
        tk={tk}
        title={t('reports.orders') || 'Orders'}
        subtitle={language === 'ar' ? 'جميع المعاملات' : 'All transactions'}
        actions={
          <GhostBtn tk={tk} icon={exporting ? Loader2 : Download} onClick={handleExport}>
            {exporting ? t('monthlyClosing.downloading') : t('reports.downloadExcel')}
          </GhostBtn>
        }
      />

      {loading ? (
        <StatStripSkeleton tk={tk} count={4} />
      ) : (
        <StatStrip
          tk={tk}
          items={[
            { label: language === 'ar' ? 'اليوم' : 'Today', value: String(todayCount) },
            {
              label: language === 'ar' ? 'المجموع' : 'Total',
              value: totalRevenue,
              money: true,
              delta: `${allTx.length} ${language === 'ar' ? 'طلبات' : 'orders'}`,
              positive: true,
            },
            { label: language === 'ar' ? 'المرتجع' : 'Refunded', value: String(refundedCount) },
          ]}
        />
      )}

      <Toolbar
        tk={tk}
        searchPlaceholder={t('reports.searchOrders')}
        searchValue={searchText}
        onSearchChange={(v) => {
          setSearchText(v);
          setFilters((prev) => ({ ...prev, ...parseOrderSearch(v) }));
          setCurrentPage(1);
        }}
        searchLabel={t('common.search')}
        searchColumnStyle={{ flex: '0 0 260px', minWidth: 260, maxWidth: 260 }}
        filters={
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: 170 }}>
              <span style={ordersFilterLabelStyle}>{t('reports.discount')}</span>
              <FilterChip
                tk={tk}
                label={t('reports.discount')}
                value={filters.has_discount}
                onChange={(v) => setFilters((f) => ({ ...f, has_discount: v }))}
                options={discountOptions}
                active={filters.has_discount !== 'all'}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: 170 }}>
              <span style={ordersFilterLabelStyle}>{t('common.status')}</span>
              <FilterChip
                tk={tk}
                label={t('common.status')}
                value={filters.status}
                onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
                options={statusOptions}
                active={filters.status !== 'all'}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: 170 }}>
              <span style={ordersFilterLabelStyle}>{t('reports.paymentMethod')}</span>
              <FilterChip
                tk={tk}
                label={t('reports.paymentMethod')}
                value={filters.payment_method}
                onChange={(v) => setFilters((f) => ({ ...f, payment_method: v }))}
                options={paymentMethodOptions}
                active={filters.payment_method !== 'all'}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: 170 }}>
              <span style={ordersFilterLabelStyle}>{t('reports.orderType')}</span>
              <FilterChip
                tk={tk}
                label={t('reports.orderType')}
                value={filters.order_type}
                onChange={(v) => setFilters((f) => ({ ...f, order_type: v }))}
                options={orderTypeOptions}
                active={filters.order_type !== 'all'}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: 170 }}>
              <span style={ordersFilterLabelStyle}>{t('sessions.cashier')}</span>
              <FilterChip
                tk={tk}
                label={t('sessions.cashier')}
                value={filters.cashier_id}
                onChange={(v) => setFilters((f) => ({ ...f, cashier_id: v }))}
                options={cashierOptions}
                active={filters.cashier_id !== 'all'}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: 170 }}>
              <span style={ordersFilterLabelStyle}>{t('pos.deliveryPerson')}</span>
              <FilterChip
                tk={tk}
                label={t('pos.deliveryPerson')}
                value={filters.delivery_person_id}
                onChange={(v) => setFilters((f) => ({ ...f, delivery_person_id: v }))}
                options={deliveryOptions}
                active={filters.delivery_person_id !== 'all'}
                onFocus={deliveryPersons.length === 0 ? loadDeliveryPersons : undefined}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: 200 }}>
              <span style={ordersFilterLabelStyle}>{t('common.from')}</span>
              <input
                type="datetime-local"
                value={filters.start_date}
                onChange={(e) => setFilters((f) => ({ ...f, start_date: e.target.value }))}
                style={{
                  height: 34, width: '100%', boxSizing: 'border-box', padding: '0 10px',
                  background: tk.surface, border: `1px solid ${tk.border}`,
                  borderRadius: 8, color: tk.text, fontFamily: 'inherit',
                  fontSize: 12, outline: 'none',
                }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: 200 }}>
              <span style={ordersFilterLabelStyle}>{t('common.to')}</span>
              <input
                type="datetime-local"
                value={filters.end_date}
                onChange={(e) => setFilters((f) => ({ ...f, end_date: e.target.value }))}
                style={{
                  height: 34, width: '100%', boxSizing: 'border-box', padding: '0 10px',
                  background: tk.surface, border: `1px solid ${tk.border}`,
                  borderRadius: 8, color: tk.text, fontFamily: 'inherit',
                  fontSize: 12, outline: 'none',
                }}
              />
            </div>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                style={{
                  alignSelf: 'flex-end',
                  height: 34, padding: '0 10px',
                  background: 'transparent', border: `1px solid ${tk.border}`,
                  borderRadius: 8, color: tk.textMuted, cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 12, display: 'flex', alignItems: 'center', gap: 5,
                }}
              >
                <X size={12} /> {t('common.clear')}
              </button>
            )}
          </>
        }
      />

      {error && (
        <div style={{
          background: tk.dangerSoft, border: `1px solid ${tk.danger}`,
          borderRadius: 8, padding: '10px 14px', marginBottom: 12,
          fontSize: 13, color: tk.dangerText,
        }}>
          {error}
        </div>
      )}

      {loading ? (
        <TableSkeleton tk={tk} colWidths={[0.8, 1.5, 1.5, 0.7, 1, 1.5, 1, 0.4]} />
      ) : (
        <DataTable
          tk={tk}
          columns={cols}
          rows={currentTransactions}
          renderCell={renderCell}
          emptyMessage={t('reports.noOrders') || 'No orders found'}
        />
      )}

      <Pagination
        tk={tk}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />

      {/* ── Order Detail Modal ──────────────────────────────────────────────── */}
      {selectedTransaction && (
        <div style={overlayStyle}>
          <div style={modalBoxStyle}>
            {/* Header */}
            <div style={{
              padding: '18px 22px', borderBottom: `1px solid ${tk.border}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              flexShrink: 0,
            }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: tk.text, marginBottom: 4 }}>
                  {t('reports.orderDetails')}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    fontFamily: '"JetBrains Mono", monospace', fontSize: 13,
                    fontWeight: 700, color: tk.accentText,
                  }}>
                    {selectedTransaction.receipt_num}
                  </span>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(selectedTransaction.receipt_num || '');
                      setReceiptCopied(true);
                      setTimeout(() => setReceiptCopied(false), 1500);
                    }}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: tk.textMuted, display: 'flex', alignItems: 'center', padding: 4,
                    }}
                    title={t('common.copy')}
                  >
                    <Copy size={13} />
                  </button>
                  {receiptCopied && (
                    <span style={{ fontSize: 11, color: tk.successText, fontWeight: 600 }}>
                      {t('common.copied')}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <StatusPill tk={tk} kind={getStatusKind(selectedTransaction.status)}>
                  {getStatusLabel(selectedTransaction.status)}
                </StatusPill>
                <button
                  onClick={() => setSelectedTransaction(null)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 30, height: 30, borderRadius: 6,
                    background: tk.sunken, border: `1px solid ${tk.border}`,
                    color: tk.textSecondary, cursor: 'pointer',
                  }}
                  aria-label={t('common.close')}
                >
                  <X size={15} />
                </button>
              </div>
            </div>

            {/* Body */}
            <div style={{ overflowY: 'auto', padding: '18px 22px', flex: 1 }}>

              {/* Meta grid */}
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                gap: 12, marginBottom: 16,
              }}>
                {[
                  { label: t('common.date'), value: formatDateTime(selectedTransaction.created_at) },
                  { label: t('sessions.cashier'), value: selectedTransaction.cashier_name || '—' },
                  { label: t('pos.orderMode'), value: selectedTransaction.delivery_region ? t('pos.delivery') : t('pos.inStore') },
                  ...(selectedTransaction.delivery_region ? [
                    { label: t('pos.deliveryRegion'), value: selectedTransaction.delivery_region },
                    { label: t('pos.customerName'), value: selectedTransaction.customer_name || t('common.deleted'), danger: !selectedTransaction.customer_name },
                    { label: t('pos.deliveryPerson'), value: selectedTransaction.delivery_person_name || (selectedTransaction.delivery_person_id === null ? t('common.deleted') : '—'), danger: selectedTransaction.delivery_person_id === null && !selectedTransaction.delivery_person_name },
                  ] : []),
                  ...(parseFloat(selectedTransaction.promo_percentage || 0) > 0 ? [
                    {
                      label: t('pos.promo'),
                      value: selectedTransaction.promo_name
                        ? `${selectedTransaction.promo_name} (${selectedTransaction.promo_percentage}%)`
                        : `${selectedTransaction.promo_percentage}%`,
                      accent: true,
                    },
                  ] : []),
                  ...(selectedTransaction.discount_percentage > 0 ? [
                    { label: t('pos.discount'), value: `${selectedTransaction.discount_percentage}%`, accent: true },
                    ...(selectedTransaction.discount_notes
                      ? [{ label: t('pos.discountNotes'), value: selectedTransaction.discount_notes }]
                      : []),
                    ...(selectedTransaction.discount_approver_name
                      ? [{
                          label: t('refunds.approvedBy'),
                          value: selectedTransaction.discount_approver_role
                            ? `${selectedTransaction.discount_approver_name} (${selectedTransaction.discount_approver_role})`
                            : selectedTransaction.discount_approver_name,
                        }]
                      : []),
                  ] : []),
                  ...(Number(selectedTransaction.tax_percentage) > 0 ? [
                    { label: t('pos.tax'), value: `${selectedTransaction.tax_percentage}%`, accent: true },
                  ] : []),
                ].map((item, i) => (
                  <div key={i} style={{
                    background: tk.sunken, border: `1px solid ${tk.border}`,
                    borderRadius: 8, padding: '10px 12px',
                  }}>
                    <div style={{ fontSize: 10.5, color: tk.textMuted, marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                      {item.label}
                    </div>
                    <div style={{
                      fontSize: 13, fontWeight: 600,
                      color: item.danger ? tk.dangerText : item.accent ? tk.warning : tk.text,
                    }}>
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>

              {/* Payment method */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                  {t('reports.paymentDetails')}
                </div>
                {selectedTransaction.payments && selectedTransaction.payments.length > 0 ? (
                  <div style={{
                    background: tk.sunken, border: `1px solid ${tk.border}`,
                    borderRadius: 8, padding: '12px 14px',
                  }}>
                    {selectedTransaction.payment_method === 'mixed' ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {selectedTransaction.payments.map((payment, index) => (
                          <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <MethodPill tk={tk} method={payment.method} t={t} />
                              {payment.method !== 'cash' && payment.transaction_ref && (
                                <div style={{ fontSize: 11, fontFamily: '"JetBrains Mono",monospace', color: tk.textMuted, marginTop: 3 }}>
                                  {t('pos.transactionNo')}: {payment.transaction_ref}
                                </div>
                              )}
                            </div>
                            <MonoMoney amount={payment.amount} tk={tk} size={13} weight={600} />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <MethodPill tk={tk} method={selectedTransaction.payment_method || 'cash'} t={t} />
                        {selectedTransaction.payment_method !== 'cash' && selectedTransaction.payments[0]?.transaction_ref && (
                          <span style={{ fontSize: 11, fontFamily: '"JetBrains Mono",monospace', color: tk.textMuted }}>
                            {t('pos.transactionNo')}: {selectedTransaction.payments[0].transaction_ref}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{
                    background: tk.sunken, border: `1px solid ${tk.border}`,
                    borderRadius: 8, padding: '10px 14px',
                  }}>
                    <MethodPill tk={tk} method={selectedTransaction.payment_method || 'cash'} t={t} />
                  </div>
                )}
              </div>

              {/* Order items */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                  {t('reports.orderItems')}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {(selectedTransaction.items || []).map((item, index) => (
                    <div key={index} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                      background: tk.sunken, border: `1px solid ${tk.border}`,
                      borderRadius: 8, padding: '10px 14px',
                    }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: tk.text, marginBottom: 2 }}>
                          {item.product_name}
                        </div>
                        <div style={{ fontSize: 11.5, color: tk.textSecondary }}>
                          {item.quantity} &times;{' '}
                          <span style={{ fontFamily: '"JetBrains Mono",monospace' }}>
                            {formatCurrency(item.unit_price)} {t('currency.egp')}
                          </span>
                        </div>
                        {item.is_offer && (
                          <OrderItemComponentsList
                            components={item.components}
                            tk={tk}
                            t={t}
                          />
                        )}
                      </div>
                      <MonoMoney amount={item.total_price} tk={tk} size={13} weight={600} />
                    </div>
                  ))}
                  {(selectedTransaction.items || []).length === 0 && (
                    <div style={{ padding: '20px', textAlign: 'center', color: tk.textMuted, fontSize: 12 }}>
                      <Package size={20} style={{ margin: '0 auto 6px', opacity: 0.4 }} />
                      No items
                    </div>
                  )}
                </div>
              </div>

              {/* Total row */}
              <div style={{
                background: tk.accentSoft, border: `1px solid ${tk.border}`,
                borderRadius: 10, padding: '14px 16px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: tk.text }}>
                  {t('common.total')}
                </span>
                <MonoMoney amount={selectedTransaction.total_amount} tk={tk} size={16} weight={700} />
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '14px 22px', borderTop: `1px solid ${tk.border}`,
              display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0,
            }}>
              <button
                type="button"
                onClick={handlePrintOrderDetail}
                disabled={orderPrintLoading}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '0 16px', height: 36,
                  background: tk.accent, border: 'none',
                  color: '#fff', borderRadius: 8,
                  opacity: orderPrintLoading ? 0.65 : 1,
                  cursor: orderPrintLoading ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                }}
              >
                <Printer size={14} />
                {orderPrintLoading ? t('common.loading') : t('common.print')}
              </button>
              <button
                onClick={() => setSelectedTransaction(null)}
                style={cancelBtnStyle}
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;
